import { v4 } from 'uuid';

import {
  defaultClosingRange,
  defaultCompoundStepCount,
  defaultCompoundStepCountOperator,
  perStepClosingRange,
  stepTypes,
} from '../../constants/compound';
import { createFilterReducer } from '../filter';
import { segmentStepFilterActions, segmentStepFilterActionsPrefix } from '../../constants/filter';

const updateInitialState = {
  segment: null,
  updating: false,
  id: undefined,
};

const pureUpdateStep = (segmentSteps, stepIndex, newProps) => {
  return [
    ...segmentSteps.slice(0, stepIndex),
    {
      ...segmentSteps[stepIndex],
      ...newProps,
    },
    ...segmentSteps.slice(stepIndex + 1),
  ];
};

const stepFilterReducer = createFilterReducer(segmentStepFilterActionsPrefix);

const defaultStep = {
  filter: stepFilterReducer(undefined, {}),
  closingRange: defaultClosingRange,
  type: stepTypes.POSITIVE,
  count: defaultCompoundStepCount,
  countOperator: defaultCompoundStepCountOperator,
};

const defaultSegment = {
  steps: [defaultStep],
  closingRange: perStepClosingRange,
};

const changeStepPosition = (steps, oldId, newId) => {
  if (oldId === newId) {
    return steps;
  }

  let result;

  if (oldId < newId) {
    result = [
      ...steps.slice(0, oldId),
      ...steps.slice(oldId + 1, newId + 1),
      undefined,
      ...steps.slice(newId + 1),
    ];
  } else {
    result = [
      ...steps.slice(0, newId),
      undefined,
      ...steps.slice(newId, oldId),
      ...steps.slice(oldId + 1),
    ];
  }

  const updatedStep = { ...steps[oldId] };

  if (oldId === 0) {
    result[0] = { ...steps[1], type: stepTypes.POSITIVE };
  } else if (newId === 0) {
    updatedStep.type = stepTypes.POSITIVE;
  }

  result[newId] = updatedStep;
  return result;
};

const update = (state = updateInitialState, action) => {
  switch (action.type) {
    case 'START_UPDATE_SEGMENT': {
      const segment = action.segment || defaultSegment;

      return {
        ...state,
        segment: {
          ...segment,
          steps: segment.steps.map((step) => ({ ...step, id: v4() })),
        },
        id: action.segment ? action.segment.id : undefined,
      };
    }

    case 'CANCEL_UPDATE_SEGMENT':
      return updateInitialState;

    case 'UPDATE_SET_SEGMENT_NAME':
      return {
        ...state,
        segment: { ...state.segment, name: action.name },
      };

    case 'UPDATE_SEGMENT':
      return {
        ...state,
        updating: true,
      };

    case 'UPDATE_SEGMENT_SUCCESS':
      return updateInitialState;

    case 'UPDATE_SEGMENT_FAILURE':
      return {
        ...state,
        updating: false,
      };

    case 'DELETE_SEGMENT_SUCCESS':
      return updateInitialState;

    case 'UPDATE_SEGMENT_ADD_STEP':
      return {
        ...state,
        segment: {
          ...state.segment,
          steps: [...state.segment.steps, { ...defaultStep, id: v4() }],
        },
      };

    case 'UPDATE_SEGMENT_CHANGE_STEP_POSITION':
      return {
        ...state,
        segment: {
          ...state.segment,
          steps: changeStepPosition(state.segment.steps, action.oldIndex, action.newIndex),
        },
      };

    case 'UPDATE_SEGMENT_SET_EVENT_NAME':
      return {
        ...state,
        segment: {
          ...state.segment,
          steps: pureUpdateStep(state.segment.steps, action.stepIndex, {
            event: action.event,
            filter: stepFilterReducer(undefined, {}),
          }),
        },
      };

    case 'UPDATE_SEGMENT_SET_CLOSING_RANGE':
      return {
        ...state,
        segment: {
          ...state.segment,
          steps: pureUpdateStep(state.segment.steps, action.stepIndex, {
            closingRange: action.closingRange,
          }),
        },
      };

    case 'UPDATE_REMOVE_SEGMENT_STEP':
      return {
        ...state,
        segment: {
          ...state.segment,
          steps: [
            ...state.segment.steps.slice(0, action.stepIndex),
            ...state.segment.steps.slice(action.stepIndex + 1),
          ],
        },
      };

    case 'UPDATE_SEGMENT_SET_STEP_TYPE':
      return {
        ...state,
        segment: {
          ...state.segment,
          steps: pureUpdateStep(state.segment.steps, action.stepIndex, {
            type: action.stepType,
          }),
        },
      };

    case 'UPDATE_SEGMENT_SET_STEP_COUNT':
      return {
        ...state,
        segment: {
          ...state.segment,
          steps: pureUpdateStep(state.segment.steps, action.stepIndex, {
            count: action.count,
            countOperator: action.operator,
          }),
        },
      };

    case 'UPDATE_SEGMENT_SET_DATE_RANGE':
      return {
        ...state,
        segment: {
          ...state.segment,
          dateRange: action.dateRange,
        },
      };

    case 'SEGMENT_SELECT_DUPLICATE_SEGMENT': {
      const segment = action.item;

      return {
        ...updateInitialState,
        segment: {
          ...segment,
          steps: segment.steps.map((step) => ({ ...step, id: v4() })),
          name: `${segment.name}_copy`,
          id: undefined,
        },
      };
    }

    case segmentStepFilterActions.ADD_FILTER_CLAUSE:
    case segmentStepFilterActions.REMOVE_FILTER_CLAUSE:
    case segmentStepFilterActions.SET_FILTER_CLAUSE_FIELD:
    case segmentStepFilterActions.SET_FILTER_CLAUSE_VALUE:
    case segmentStepFilterActions.SET_CLAUSES_CONCAT_OPERATOR:
    case segmentStepFilterActions.SET_FILTER_CLAUSE_OPERATOR:
      return {
        ...state,
        segment: {
          ...state.segment,
          steps: pureUpdateStep(state.segment.steps, action.stepIndex, {
            filter: stepFilterReducer(state.segment.steps[action.stepIndex].filter, action),
          }),
        },
      };

    default:
      return state;
  }
};

export default update;

export const isUpdating = (state) => state.updating;

export const getUpdating = (state) => state.segment;

export const getUpdatingId = (state) => state.id;
